import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { TemplateDragGhost } from '@/components/app/templates/template-drag-ghost'
import {
  type PlacementCanvasApi,
  type PlacementIntrinsicSize,
  usePlacementSession,
} from '@/hooks/use-placement-session'
import type { CenterCanvasHandle } from '@/components/app/editor/center-canvas'
import { useBackgroundPlacementOptional } from '@/contexts/background-placement-context'
import { useClikArtPlacementOptional } from '@/contexts/clikart-placement-context'
import { usePhotoPlacementOptional } from '@/contexts/photo-placement-context'
import { useStableCallbacks } from '@/hooks/use-stable-callbacks'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import {
  TEMPLATE_DRAG_GHOST_OPACITY,
  TEMPLATE_KEYBOARD_DEFAULT_CENTER_FRACTION,
} from '@/lib/templates/template-placement-config'
import type { TemplateListItem } from '@/lib/templates/types/template-list-item'

export type TemplatePlacementCanvasApi = PlacementCanvasApi

type TemplatePlacementContextValue = {
  beginPointerDrag: (
    template: TemplateListItem,
    clientX: number,
    clientY: number,
    thumbRect: DOMRect,
    pointerId: number,
    pointerCaptureEl?: HTMLElement | null
  ) => void
  beginKeyboardCarry: (template: TemplateListItem, thumbRect: DOMRect) => void
  cancelDrag: () => void
  isDraggingTemplate: boolean
  draggingTemplateId: string | null
  isKeyboardCarry: boolean
  /** A template drag is over the pasteboard, so a drop here would place it (the "drag-over" state). */
  isOverDropSurface: boolean
  /** A template dropped on a spread that already has content, held until the user answers the
   *  "place over existing content?" prompt. Null when nothing is waiting. */
  pendingPlacement: TemplateListItem | null
  confirmPendingPlacement: () => void
  cancelPendingPlacement: () => void
  registerCanvasApi: (api: TemplatePlacementCanvasApi | null) => void
}

const TemplatePlacementContext = createContext<TemplatePlacementContextValue | null>(null)

export function useTemplatePlacementOptional(): TemplatePlacementContextValue | null {
  return useContext(TemplatePlacementContext)
}

type TemplatePlacementProviderProps = {
  children: React.ReactNode
  centerCanvasRef: React.RefObject<CenterCanvasHandle | null>
  /** Read at drop time: a spread with content asks before the template goes on top of it. Omitted,
   *  every drop places straight away. */
  shapeManagerRef?: React.MutableRefObject<ShapeManagerRef | null>
  /** A prompt left open belongs to the spread it was raised on, so it closes when this changes. */
  spreadId?: string | null
  onScreenReaderAnnounce?: (message: string) => void
  /** Called when a template is dropped on the spread (or placed from the keyboard). The handler
   *  loads the template's elements and adds them; where it was dropped does not matter, because a
   *  template keeps its own coordinates. */
  onPlaceTemplate?: (template: TemplateListItem) => void
}

/**
 * Drag a template from the Templates tray onto the spread. Drives the shared
 * `usePlacementSession`, like ClikArt and Photos, but a template is never placed at the drop point
 * and never fills a shape: it always becomes new elements at its own coordinates, on top of
 * whatever is already on the spread.
 */
export function TemplatePlacementProvider({
  children,
  centerCanvasRef,
  shapeManagerRef,
  spreadId,
  onScreenReaderAnnounce,
  onPlaceTemplate,
}: TemplatePlacementProviderProps) {
  const intrinsicSizeRef = useRef<PlacementIntrinsicSize | null>(null)
  const [pendingPlacement, setPendingPlacementState] = useState<TemplateListItem | null>(null)
  // The prompt's template, kept beside the state so the answer handlers read the current one from
  // an event or an effect without being rebuilt each time it changes (they are handed out stable).
  const pendingRef = useRef<TemplateListItem | null>(null)

  const setPendingPlacement = useCallback((next: TemplateListItem | null) => {
    pendingRef.current = next
    setPendingPlacementState(next)
  }, [])

  // A drop on a spread with content waits for an answer (US-17); a blank spread places at once.
  // The check reads the live manager, so undoing the only element makes the next drop prompt-free.
  const placeOrAsk = useCallback(
    (target: TemplateListItem, announce: (message: string) => void) => {
      if ((shapeManagerRef?.current?.getShapes().length ?? 0) > 0) {
        setPendingPlacement(target)
        announce(
          `Place ${target.templateName} over existing content? Choose Continue to place it or Cancel to keep the spread as it is.`
        )

        return
      }

      onPlaceTemplate?.(target)
      announce(`Placed ${target.templateName} on the spread`)
    },
    [onPlaceTemplate, setPendingPlacement, shapeManagerRef]
  )

  const session = usePlacementSession<TemplateListItem>({
    centerCanvasRef,
    intrinsicSizeRef,
    onScreenReaderAnnounce,
    // A template is placed on the spread only: over the grey pasteboard beside it the drag shows no
    // drag-over state and a release places nothing (WMPCM-10906 AC 3).
    dropArea: 'spread',
    // Never raised: no shape is a template drop target (`TemplatePlacementCanvasBridge`).
    blockedDropMessage: '',
    keyboardCenterFraction: TEMPLATE_KEYBOARD_DEFAULT_CENTER_FRACTION,
    displayNameOf: (target) => target.templateName,
    placeAtCanvas: (target, _centerX, _centerY, { announce }) => placeOrAsk(target, announce),
    fillShape: (target, _hit, _point, { announce }) => placeOrAsk(target, announce),
  })

  const { item, beginPointerDrag, beginKeyboardCarry, cancelDrag, registerCanvasApi } = session
  const { isActive, isKeyboardCarry, ghost } = session

  // Like ClikArt, templates have no touch long-press, so the pointer type is pinned to mouse.
  // Picking up another template, by pointer or keyboard, drops an unanswered prompt.
  const beginTemplatePointerDrag = useCallback<TemplatePlacementContextValue['beginPointerDrag']>(
    (target, clientX, clientY, rect, pointerId, pointerCaptureEl = null) => {
      setPendingPlacement(null)
      beginPointerDrag(target, clientX, clientY, rect, pointerId, 'mouse', pointerCaptureEl)
    },
    [beginPointerDrag, setPendingPlacement]
  )

  const beginTemplateKeyboardCarry = useCallback<
    TemplatePlacementContextValue['beginKeyboardCarry']
  >(
    (target, thumbRect) => {
      setPendingPlacement(null)
      beginKeyboardCarry(target, thumbRect)
    },
    [beginKeyboardCarry, setPendingPlacement]
  )

  useEffect(() => {
    setPendingPlacement(null)
  }, [setPendingPlacement, spreadId])

  // Stable: the Templates panel cancels from an unmount cleanup, which must not re-run (and so
  // must not cancel) just because a handler was rebuilt.
  const { confirmPendingPlacement, cancelPendingPlacement } = useStableCallbacks({
    confirmPendingPlacement: () => {
      const target = pendingRef.current

      if (!target) return

      setPendingPlacement(null)
      onPlaceTemplate?.(target)
      onScreenReaderAnnounce?.(`Placed ${target.templateName} on the spread`)
    },
    cancelPendingPlacement: () => {
      const target = pendingRef.current

      if (!target) return

      setPendingPlacement(null)
      onScreenReaderAnnounce?.(`Cancelled placing ${target.templateName}`)
    },
  })

  // Starting any other tray drag (photo, ClikArt or background) is a "no" to the prompt: the user
  // has moved on, and the held template should not be placed from under that drag.
  const isDraggingPhoto = usePhotoPlacementOptional()?.isDraggingPhoto === true
  const isDraggingClikArt = useClikArtPlacementOptional()?.isDraggingClikArt === true
  const isDraggingBackground = useBackgroundPlacementOptional()?.isDraggingBackground === true
  const isOtherTrayDragActive = isDraggingPhoto || isDraggingClikArt || isDraggingBackground

  useEffect(() => {
    if (isOtherTrayDragActive) {
      cancelPendingPlacement()
    }
  }, [isOtherTrayDragActive, cancelPendingPlacement])

  const isOverDropSurface = isActive && ghost?.overCanvas === true

  const contextValue = useMemo<TemplatePlacementContextValue>(
    () => ({
      beginPointerDrag: beginTemplatePointerDrag,
      beginKeyboardCarry: beginTemplateKeyboardCarry,
      cancelDrag,
      isDraggingTemplate: isActive,
      draggingTemplateId: item?.id ?? null,
      isKeyboardCarry,
      isOverDropSurface,
      pendingPlacement,
      confirmPendingPlacement,
      cancelPendingPlacement,
      registerCanvasApi,
    }),
    [
      beginTemplateKeyboardCarry,
      beginTemplatePointerDrag,
      cancelDrag,
      cancelPendingPlacement,
      confirmPendingPlacement,
      isActive,
      isKeyboardCarry,
      isOverDropSurface,
      item?.id,
      pendingPlacement,
      registerCanvasApi,
    ]
  )

  return (
    <TemplatePlacementContext.Provider value={contextValue}>
      {children}
      {item != null &&
        ghost != null &&
        createPortal(
          <TemplateDragGhost template={item} opacity={TEMPLATE_DRAG_GHOST_OPACITY} {...ghost} />,
          document.body
        )}
    </TemplatePlacementContext.Provider>
  )
}
